import { service } from '@ember/service';
import Route from '@ember/routing/route';
import { findRecord } from '@warp-drive/utilities/json-api';
import type SessionContextService from 'tio-employee/services/session-context';
import type { Store } from '@warp-drive/core';
import type { PersonExtension as PersonModel } from 'tio-data/data/resources/person.ext';
import type EmployeeService from 'tio-employee/services/employee';
import type PartnerService from 'tio-employee/services/partner';
import type ReimaginedDataService from 'tio-employee/services/reimagined-data';
import type BetaExperienceService from 'tio-employee/services/beta-experience';
import type DemoModeService from 'tio-employee/services/demo-mode';
import type { EmployerContributions } from 'tio-employee/services/reimagined-data';
import type { LoanAccount } from 'tio-employee/utils/slr/loan-account';
import { benefitFlags } from 'tio-employee/utils/beta/benefit-flags';
import { benefitRows, type BenefitRows } from 'tio-employee/utils/beta/benefit-rows';

export interface AccountActivityAccountsRouteModel {
  person: PersonModel;
  /** The product rows the side nav shows this user; the benefits zone mirrors them. */
  benefitRows: BenefitRows;
  /** Live employer-contribution totals for an SLR member in the beta experience, otherwise null. */
  employerContributions: EmployerContributions | null;
  /** Each linked account rolled up for the beta's progress hero and cards; empty outside the beta. */
  loanAccounts: LoanAccount[];
}

export default class AuthenticatedAccountActivityAccountsRoute extends Route {
  @service declare store: Store;
  @service declare sessionContext: SessionContextService;
  @service declare employee: EmployeeService;
  @service declare partner: PartnerService;
  @service declare reimaginedData: ReimaginedDataService;
  @service('beta-experience') declare betaExperience: BetaExperienceService;
  @service declare demoMode: DemoModeService;

  /**
   * Loads the person with accounts, loans and plans, plus what the beta zones need.
   *
   * @returns the person, the benefit rows the nav shows them, and live contribution totals or null
   */
  async model(): Promise<AccountActivityAccountsRouteModel> {
    // Refresh contribution data when navigating to this route (fixes DEV-4638)
    // This ensures contribution amounts display after loan upload without requiring a page refresh
    if (this.employee.contributionsState.isFinished) {
      this.employee.contributionsState.retry();
    }
    if (this.employee.futurePayments.isFinished) {
      this.employee.futurePayments.retry();
    }

    const personId = this.sessionContext.user.person.id!;
    const { content: personContent } = (await this.store.request(
      findRecord<PersonModel>('person', personId, {
        include:
          'accounts.loans,accounts.financial-institution,accounts.postal-address,employees.plan',
        reload: true,
      })
    )) as { content: { data: PersonModel } };
    const person = personContent.data;

    const { wellnessData } = this.modelFor('authenticated') as {
      wellnessData: Record<string, unknown> | null;
    };
    const rows = benefitRows(
      benefitFlags({
        partner: this.partner,
        employee: this.employee,
        sessionContext: this.sessionContext,
        wellnessData,
        scenario: this.demoMode.scenarioFeatures,
      })
    );

    let employerContributions: EmployerContributions | null = null;
    let loanAccounts: LoanAccount[] = [];
    if (this.betaExperience.isEnabled) {
      ({ loans: loanAccounts } = await this.reimaginedData.fetchLoansData());
      if (rows.slr) {
        const { contributions, isLiveData } =
          await this.reimaginedData.fetchEmployerContributions();
        employerContributions = isLiveData ? contributions : null;
      }
    }

    return { person, benefitRows: rows, employerContributions, loanAccounts };
  }
}
